import { useCallback, useEffect, useRef, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { isStandalone } from "@/utils/standalone";
import {
  A2HS_BLOCKED_MAX_RETRIES,
  A2HS_BLOCKED_RETRY_MS,
  getA2HSDelayMs,
  isA2HSBlockedByOverlay,
} from "@/utils/a2hsTiming";
import {
  A2HS_EVENT_AFTER_FIRST_DAY,
  A2HS_EVENT_AFTER_FIRST_PLAN,
  A2HS_EVENT_AFTER_FIRST_RECIPE,
  A2HS_EVENT_AFTER_FIRST_WEEK,
  A2HS_EVENT_AFTER_TWO_RECIPES,
  type A2HSTriggerSource,
} from "@/utils/a2hsTypes";

export type { A2HSTriggerSource };
export {
  A2HS_EVENT_AFTER_FIRST_PLAN,
  A2HS_EVENT_AFTER_FIRST_RECIPE,
  A2HS_EVENT_AFTER_TWO_RECIPES,
  A2HS_EVENT_AFTER_FIRST_DAY,
  A2HS_EVENT_AFTER_FIRST_WEEK,
};

export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const STORAGE_KEY_COUNT = "a2hs_attempt_count";
const STORAGE_KEY_NEXT_ELIGIBLE_AT = "a2hs_next_eligible_at";
const STORAGE_KEY_FOREVER = "a2hs_dismissed_forever";
const STORAGE_KEY_TRIGGER_SOURCE = "a2hs_trigger_source";

const MAX_ATTEMPTS = 3;
const LATER_1_DAYS = 3;
const LATER_2_DAYS = 7;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

function readStoredCount(): number {
  try {
    if (typeof window === "undefined") return 0;
    const s = localStorage.getItem(STORAGE_KEY_COUNT);
    const n = s != null ? parseInt(s, 10) : 0;
    return Number.isFinite(n) ? Math.max(0, Math.min(n, MAX_ATTEMPTS)) : 0;
  } catch {
    return 0;
  }
}

function readStoredNextEligibleAt(): number | null {
  try {
    if (typeof window === "undefined") return null;
    const s = localStorage.getItem(STORAGE_KEY_NEXT_ELIGIBLE_AT);
    const n = s != null ? parseInt(s, 10) : NaN;
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function readDismissedForever(): boolean {
  try {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(STORAGE_KEY_FOREVER) === "1";
  } catch {
    return false;
  }
}

function readTriggerSource(): "" | A2HSTriggerSource {
  try {
    if (typeof window === "undefined") return "";
    const s = localStorage.getItem(STORAGE_KEY_TRIGGER_SOURCE);
    if (s === "plan" || s === "recipe" || s === "day" || s === "week") return s;
    return "";
  } catch {
    return "";
  }
}

function writeStored(count: number, nextEligibleAt: number | null, forever: boolean): void {
  try {
    if (typeof window === "undefined") return;
    localStorage.setItem(STORAGE_KEY_COUNT, String(count));
    if (nextEligibleAt != null) {
      localStorage.setItem(STORAGE_KEY_NEXT_ELIGIBLE_AT, String(nextEligibleAt));
    } else {
      localStorage.removeItem(STORAGE_KEY_NEXT_ELIGIBLE_AT);
    }
    if (forever) {
      localStorage.setItem(STORAGE_KEY_FOREVER, "1");
    } else {
      localStorage.removeItem(STORAGE_KEY_FOREVER);
    }
  } catch {
    // ignore
  }
}

function setTriggerSource(source: A2HSTriggerSource): void {
  try {
    if (typeof window === "undefined") return;
    localStorage.setItem(STORAGE_KEY_TRIGGER_SOURCE, source);
  } catch {
    // ignore
  }
}

function isRunningAsInstalledPWA(): boolean {
  if (typeof window === "undefined") return false;
  if (window.matchMedia("(display-mode: standalone)").matches) return true;
  if ((window.navigator as { standalone?: boolean }).standalone === true) return true;
  return false;
}

export function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    ((navigator as { platform?: string }).platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

function canShowNow(): boolean {
  if (readDismissedForever()) return false;
  const count = readStoredCount();
  if (count >= MAX_ATTEMPTS) return false;
  const nextAt = readStoredNextEligibleAt();
  if (nextAt != null && Date.now() < nextAt) return false;
  return true;
}

function readDeferredPromptFromWindow(): BeforeInstallPromptEvent | null {
  if (typeof window === "undefined") return null;
  return (window as Window & { __beforeInstallPromptEvent?: BeforeInstallPromptEvent })
    .__beforeInstallPromptEvent ?? null;
}

export function usePWAInstall() {
  const { user } = useAuth();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(() =>
    readDeferredPromptFromWindow()
  );
  const [isInstalled, setIsInstalled] = useState(isRunningAsInstalledPWA);
  const [showModal, setShowModal] = useState(false);
  const { toast } = useToast();
  const isInstalledRef = useRef(isInstalled);
  const scheduleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  isInstalledRef.current = isInstalled;

  const clearScheduledShow = useCallback(() => {
    if (scheduleTimeoutRef.current) {
      clearTimeout(scheduleTimeoutRef.current);
      scheduleTimeoutRef.current = null;
    }
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  }, []);

  const tryOpenInstallModal = useCallback((retry = 0) => {
    if (isInstalledRef.current || isStandalone()) return;
    if (!canShowNow()) return;
    if (isA2HSBlockedByOverlay()) {
      if (retry < A2HS_BLOCKED_MAX_RETRIES) {
        retryTimeoutRef.current = setTimeout(() => {
          retryTimeoutRef.current = null;
          tryOpenInstallModal(retry + 1);
        }, A2HS_BLOCKED_RETRY_MS);
      }
      return;
    }
    setShowModal(true);
  }, []);

  useEffect(() => {
    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    const onPromptAvailable = (e: Event) => {
      const detail = (e as CustomEvent<BeforeInstallPromptEvent>).detail;
      if (detail) setDeferredPrompt(detail);
    };

    const onInstalled = () => {
      setIsInstalled(true);
      setShowModal(false);
      clearScheduledShow();
      setDeferredPrompt(null);
      writeStored(MAX_ATTEMPTS, null, true);
      toast({
        title: "Готово",
        description: "Mom Recipes добавлен на главный экран.",
      });
    };

    const existing = readDeferredPromptFromWindow();
    if (existing) setDeferredPrompt(existing);

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("a2hs-prompt-available", onPromptAvailable as EventListener);
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("a2hs-prompt-available", onPromptAvailable as EventListener);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [toast, clearScheduledShow]);

  useEffect(() => {
    if (!user) return;
    if (isInstalled) return;
    if (isStandalone()) return;
    if (!canShowNow()) return;
    if (readTriggerSource() !== "") return;

    const scheduleShow = (trigger: A2HSTriggerSource) => {
      if (readTriggerSource() !== "") return;
      setTriggerSource(trigger);
      clearScheduledShow();

      const delayMs = getA2HSDelayMs(trigger);
      if (import.meta.env.DEV) {
        console.log("[DEBUG] a2hs scheduled from", trigger, "in", delayMs, "ms");
      }

      scheduleTimeoutRef.current = setTimeout(() => {
        scheduleTimeoutRef.current = null;
        tryOpenInstallModal(0);
      }, delayMs);
    };

    const onFirstPlan = () => scheduleShow("plan");
    const onFirstRecipe = () => scheduleShow("recipe");
    const onTwoRecipes = () => scheduleShow("recipe");
    const onFirstDay = () => scheduleShow("day");
    const onFirstWeek = () => scheduleShow("week");

    window.addEventListener(A2HS_EVENT_AFTER_FIRST_PLAN, onFirstPlan);
    window.addEventListener(A2HS_EVENT_AFTER_FIRST_RECIPE, onFirstRecipe);
    window.addEventListener(A2HS_EVENT_AFTER_TWO_RECIPES, onTwoRecipes);
    window.addEventListener(A2HS_EVENT_AFTER_FIRST_DAY, onFirstDay);
    window.addEventListener(A2HS_EVENT_AFTER_FIRST_WEEK, onFirstWeek);

    return () => {
      window.removeEventListener(A2HS_EVENT_AFTER_FIRST_PLAN, onFirstPlan);
      window.removeEventListener(A2HS_EVENT_AFTER_FIRST_RECIPE, onFirstRecipe);
      window.removeEventListener(A2HS_EVENT_AFTER_TWO_RECIPES, onTwoRecipes);
      window.removeEventListener(A2HS_EVENT_AFTER_FIRST_DAY, onFirstDay);
      window.removeEventListener(A2HS_EVENT_AFTER_FIRST_WEEK, onFirstWeek);
      clearScheduledShow();
    };
  }, [user, isInstalled, clearScheduledShow, tryOpenInstallModal]);

  const promptInstall = async () => {
    const prompt = deferredPrompt ?? readDeferredPromptFromWindow();
    if (!prompt) return;
    await prompt.prompt();
    await prompt.userChoice;
  };

  const dismissModal = useCallback(
    (options?: { skipIncrement?: boolean }) => {
      setShowModal(false);
      if (options?.skipIncrement) return;

      const count = readStoredCount();
      const newCount = Math.min(count + 1, MAX_ATTEMPTS);
      const now = Date.now();

      let nextEligibleAt: number | null = null;
      let forever = false;

      if (newCount >= MAX_ATTEMPTS) {
        forever = true;
      } else if (newCount === 1) {
        nextEligibleAt = now + LATER_1_DAYS * MS_PER_DAY;
      } else if (newCount === 2) {
        nextEligibleAt = now + LATER_2_DAYS * MS_PER_DAY;
      }

      writeStored(newCount, nextEligibleAt, forever);

      if (import.meta.env.DEV) {
        console.log(
          "[DEBUG] a2hs dismissed count=",
          newCount,
          forever ? "forever" : "nextEligibleAt=" + nextEligibleAt
        );
      }
    },
    []
  );

  useEffect(() => () => clearScheduledShow(), [clearScheduledShow]);

  const canInstall = Boolean(deferredPrompt ?? readDeferredPromptFromWindow()) && !isInstalled;
  const isIOSDevice = isIOS();
  const hasInstallOption = !isInstalled && (canInstall || isIOSDevice || !isStandalone());

  const installPromptTriggerSource = showModal ? readTriggerSource() : "";

  return {
    canInstall,
    promptInstall,
    showModal,
    dismissModal,
    isIOSDevice,
    isInstalled,
    hasInstallOption,
    installPromptTriggerSource,
  };
}
